// HistoryView.tsx
// Created by Taulant Geci

import React, { useState, useEffect } from 'react';
import { MatchGeneratorController } from '../modules/MatchGeneratorController';
import { MatchHistory } from '../models/types';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHistory, 
  faChevronDown, 
  faChevronUp, 
  faMapMarkerAlt, 
  faUsers, 
  faUser, 
  faClock, 
  faEdit, 
  faUserSlash,
  faCalendarAlt,
  faTrophy,
  faList,
  faCheckCircle,
  faChartBar,
  faPlus,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import { MatchResultModal } from './MatchResultModal';

interface HistoryViewProps {
  controller: MatchGeneratorController;
}

export const HistoryView: React.FC<HistoryViewProps> = ({ controller }) => {
  const { t } = useTranslation();
  const [state, setState] = useState(controller.getState());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [expandedGenerations, setExpandedGenerations] = useState<Set<string>>(new Set());
  const [modalMatch, setModalMatch] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showingManualMatchEntry, setShowingManualMatchEntry] = useState(false);
  const [showingPlayerStats, setShowingPlayerStats] = useState(false);
  const [showingPartnershipMatrix, setShowingPartnershipMatrix] = useState(false);
  
  useEffect(() => {
    const unsubscribe = controller.subscribe(setState);
    return unsubscribe;
  }, [controller]);

  const matchHistory = state.matchHistory;

  console.log('HistoryView - matchHistory:', matchHistory);

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const toggleGeneration = (generationId: string) => {
    const newExpanded = new Set(expandedGenerations);
    if (newExpanded.has(generationId)) {
      newExpanded.delete(generationId);
    } else {
      newExpanded.add(generationId);
    }
    setExpandedGenerations(newExpanded);
  };

  const handleOpenResultModal = (match: any) => {
    setModalMatch(match);
    setIsModalOpen(true);
  };

  const handleCloseResultModal = () => {
    setIsModalOpen(false);
    setModalMatch(null);
  };

  const handleResultSaved = () => {
    // Force re-render by updating state
    setState(controller.getState());
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('de-DE', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Group history by date
  const groupedHistory = matchHistory.reduce((groups: { [key: string]: MatchHistory[] }, entry) => {
    const dateKey = entry.timestamp.toDateString();
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(entry);
    return groups;
  }, {});

  console.log('HistoryView - groupedHistory:', groupedHistory);

  // Sort dates (newest first) and take first few entries as "detailed"
  const sortedDates = Object.keys(groupedHistory).sort((a, b) => 
    new Date(b).getTime() - new Date(a).getTime()
  );

  if (matchHistory.length === 0) {
    return (
      <div className="history-view">
        <div className="empty-state">
          <FontAwesomeIcon icon={faHistory} size="3x" className="empty-icon" />
          <h3>Keine Matches generiert</h3>
          <p>Generiere Matches im "Matches" Tab, um sie hier zu sehen</p>
        </div>
      </div>
    );
  }

  return (
    <div className="history-view">
      <div className="view-header">
        <h1 className="view-title">
          <FontAwesomeIcon icon={faHistory} className="me-2" />
          {t('matchHistory')}
        </h1>
        
        <div className="header-controls">
          <button
            className={`stats-toggle-btn ${showingPlayerStats ? 'active' : ''}`}
            onClick={() => setShowingPlayerStats(!showingPlayerStats)}
            title={t('playerStatistics') as string}
          >
            <FontAwesomeIcon icon={faChartBar} />
          </button>
          
          <button
            className={`stats-toggle-btn ${showingPartnershipMatrix ? 'active' : ''}`}
            onClick={() => setShowingPartnershipMatrix(!showingPartnershipMatrix)}
            title={t('partnershipMatrix') as string}
          >
            <FontAwesomeIcon icon={faUsers} />
          </button>
          
          <button
            className="add-match-btn"
            onClick={() => setShowingManualMatchEntry(true)}
            title={t('addManualMatch') as string}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
      </div>

      <div className="main-content">
        {/* Statistics Components */}
        {showingPlayerStats && (
          <PlayerStatisticsTable controller={controller} />
        )}
        
        {showingPartnershipMatrix && (
          <PartnershipMatrix controller={controller} />
        )}

        {sortedDates.map((dateKey) => {
          const isToday = dateKey === new Date().toDateString();
          const detailedCount = isToday ? 3 : 1; // Show 3 detailed for today, 1 for other days
          const detailedGenerations = groupedHistory[dateKey].slice(0, detailedCount);
          const collapsedGenerations = groupedHistory[dateKey].slice(detailedCount);
          const sectionId = `date-${dateKey}`;

          return (
            <div key={dateKey} className="history-date-section">
              {/* Date Header */}
              <div className="section-header">
                <div className="section-title">
                  <FontAwesomeIcon icon={faCalendarAlt} className="section-icon" />
                  {isToday ? t('today') : formatDate(new Date(dateKey))}
                </div>
                <div className="section-badge">
                  <FontAwesomeIcon icon={faList} />
                  {groupedHistory[dateKey].length} {t('draws')}
                </div>
              </div>

              {/* Detailed Generations */}
              {detailedGenerations.map((generation) => (
                <div key={generation.id} className="generation-detailed card-ios">
                  <div className="generation-header">
                    <div className="generation-time">
                      <FontAwesomeIcon icon={faClock} className="me-2" />
                      {formatTime(generation.timestamp)}
                    </div>
                    <div className="generation-stats">
                      <span className="match-count">
                        <FontAwesomeIcon icon={faTrophy} className="me-1" />
                        {generation.matches.length} Matches
                      </span>
                    </div>
                  </div>

                  {/* Matches */}
                  <div className="matches-list">
                    {generation.matches.map((match) => (
                      <div key={match.id} className="match-item">
                        <div className="match-court">
                          <div className="court-circle">
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="court-icon" />
                            <div>{t('court_number', { number: match.court.number })}</div>
                          </div>
                        </div>
                        <div className="match-details">
                          <div className="match-type">
                            <FontAwesomeIcon 
                              icon={match.isDoubles ? faUsers : faUser} 
                              style={{ marginRight: '0.375rem' }} 
                            />
                            {match.isDoubles ? t('doubles') : t('singles')}
                          </div>
                          <div className="match-players">
                            {match.isDoubles ? (
                              <>
                                <span className="team">
                                  <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.75rem', marginRight: '0.25rem' }} />
                                  {match.players[0]?.name} & {match.players[1]?.name}
                                </span>
                                <span className="match-vs">
                                  {t('vs')}
                                </span>
                                <span className="team">
                                  <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.75rem', marginRight: '0.25rem' }} />
                                  {match.players[2]?.name} & {match.players[3]?.name}
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="team">
                                  <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.75rem', marginRight: '0.25rem' }} />
                                  {match.players[0]?.name}
                                </span>
                                <span className="match-vs">
                                  {t('vs')}
                                </span>
                                <span className="team">
                                  <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.75rem', marginRight: '0.25rem' }} />
                                  {match.players[1]?.name}
                                </span>
                              </>
                            )}
                          </div>
                          {/* Add Result Button */}
                          <div className="match-result-action">
                            {(() => {
                              const existingResult = controller.getMatchResult(match.id);
                              return existingResult ? (
                                <div className="result-recorded">
                                  <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                  {t('resultRecorded')}
                                </div>
                              ) : (
                                <button className="btn btn-outline-primary btn-sm" onClick={() => handleOpenResultModal(match)}>
                                  <FontAwesomeIcon icon={faEdit} className="me-1" />
                                  {t('recordResult')}
                                </button>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Unmatched Players */}
                  {generation.unmatchedPlayers.length > 0 && (
                    <div className="unmatched-section">
                      <div className="unmatched-header">
                        <FontAwesomeIcon icon={faUserSlash} className="me-2" />
                        {t('notAssigned')}: {generation.unmatchedPlayers.map(p => p.name).join(', ')}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Collapsed Generations */}
              {collapsedGenerations.length > 0 && (
                <div className="collapsed-section">
                  <button
                    className="collapsed-toggle card-ios"
                    onClick={() => toggleSection(sectionId)}
                  >
                    <div className="collapsed-info">
                      <span className="collapsed-count">
                        {collapsedGenerations.length} {t('moreDraws')}
                      </span>
                      <span className="collapsed-time">
                        {formatTime(collapsedGenerations[0].timestamp)} - {formatTime(collapsedGenerations[collapsedGenerations.length - 1].timestamp)}
                      </span>
                    </div>
                    <FontAwesomeIcon 
                      icon={expandedSections.has(sectionId) ? faChevronUp : faChevronDown}
                      className="toggle-icon"
                    />
                  </button>

                  {/* Expanded Collapsed Items */}
                  {expandedSections.has(sectionId) && (
                    <div className="expanded-collapsed">
                      {collapsedGenerations.map((generation) => (
                        <div key={generation.id} className="generation-collapsed-item card-ios">
                          <button
                            className="generation-summary-header"
                            onClick={() => toggleGeneration(generation.id)}
                          >
                            <div className="summary-time">
                              <FontAwesomeIcon icon={faClock} className="me-2" />
                              {formatTime(generation.timestamp)}
                            </div>
                            <div className="summary-stats">
                              <span className="match-count">
                                {generation.matches.length} Matches
                              </span>
                              {generation.unmatchedPlayers.length > 0 && (
                                <span className="unmatched-count">
                                  <FontAwesomeIcon icon={faUserSlash} className="me-1" />
                                  {generation.unmatchedPlayers.length}
                                </span>
                              )}
                            </div>
                            <FontAwesomeIcon 
                              icon={expandedGenerations.has(generation.id) ? faChevronUp : faChevronDown}
                              className="expand-icon"
                            />
                          </button>

                          {/* Expanded Generation Details */}
                          {expandedGenerations.has(generation.id) && (
                            <div className="generation-expanded-content">
                              {/* Matches */}
                              <div className="matches-list">
                                {generation.matches.map((match) => (
                                  <div key={match.id} className="match-item">
                                    <div className="match-court">
                                      <div className="court-circle">
                                        <FontAwesomeIcon icon={faMapMarkerAlt} className="court-icon" />
                                        <div>{t('court_number', { number: match.court.number })}</div>
                                      </div>
                                    </div>
                                    <div className="match-details">
                                      <div className="match-type">
                                        <FontAwesomeIcon 
                                          icon={match.isDoubles ? faUsers : faUser} 
                                          style={{ marginRight: '0.375rem' }} 
                                        />
                                        {match.isDoubles ? t('doubles') : t('singles')}
                                      </div>
                                      <div className="match-players">
                                        {match.isDoubles ? (
                                          <>
                                            <span className="team">
                                              <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.75rem', marginRight: '0.25rem' }} />
                                              {match.players[0]?.name} & {match.players[1]?.name}
                                            </span>
                                            <span className="match-vs">
                                              {t('vs')}
                                            </span>
                                            <span className="team">
                                              <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.75rem', marginRight: '0.25rem' }} />
                                              {match.players[2]?.name} & {match.players[3]?.name}
                                            </span>
                                          </>
                                        ) : (
                                          <>
                                            <span className="team">
                                              <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.75rem', marginRight: '0.25rem' }} />
                                              {match.players[0]?.name}
                                            </span>
                                            <span className="match-vs">
                                              {t('vs')}
                                            </span>
                                            <span className="team">
                                              <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.75rem', marginRight: '0.25rem' }} />
                                              {match.players[1]?.name}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                      {/* Add Result Button */}
                                      <div className="match-result-action">
                                        {(() => {
                                          const existingResult = controller.getMatchResult(match.id);
                                          return existingResult ? (
                                            <div className="result-recorded">
                                              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                              {t('resultRecorded')}
                                            </div>
                                          ) : (
                                            <button className="btn btn-outline-primary btn-sm" onClick={() => handleOpenResultModal(match)}>
                                              <FontAwesomeIcon icon={faEdit} className="me-1" />
                                              {t('recordResult')}
                                            </button>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {/* Unmatched Players */}
                              {generation.unmatchedPlayers.length > 0 && (
                                <div className="unmatched-section">
                                  <div className="unmatched-header">
                                    <FontAwesomeIcon icon={faUserSlash} className="me-2" />
                                    Nicht zugeordnet: {generation.unmatchedPlayers.map(p => p.name).join(', ')}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isModalOpen && (
        <MatchResultModal
          match={modalMatch}
          controller={controller}
          isOpen={isModalOpen}
          onClose={handleCloseResultModal}
          onSaved={handleResultSaved}
        />
      )}
    </div>
  );
};

// iOS-style Player Statistics Table Component
interface PlayerStatisticsTableProps {
  controller: MatchGeneratorController;
}

const PlayerStatisticsTable: React.FC<PlayerStatisticsTableProps> = ({ controller }) => {
  const { t } = useTranslation();
  const [state, setState] = useState(controller.getState());

  useEffect(() => {
    const unsubscribe = controller.subscribe(setState);
    return unsubscribe;
  }, [controller]);

  const playerStats = controller.getFilteredPlayerStatistics();

  if (state.allPlayers.length === 0) {
    return (
      <div className="player-statistics-table">
        <div className="stats-header">
          <FontAwesomeIcon icon={faChartBar} />
          <span className="stats-title">{t('playerStatistics')}</span>
          <span className="stats-subtitle">{t('basedOnRecentMatches')}</span>
        </div>
        <div className="empty-stats">
          <p>{t('noPlayersAvailable')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="player-statistics-table">
      <div className="stats-header">
        <FontAwesomeIcon icon={faChartBar} />
        <span className="stats-title">{t('playerStatistics')}</span>
        <span className="stats-subtitle">{t('basedOnRecentMatches')}</span>
      </div>
      
      <div className="stats-table-container">
        <table className="ios-stats-table">
          <thead>
            <tr>
              <th className="player-col">{t('player')}</th>
              <th className="pauses-col">{t('pauses')}</th>
              <th className="doubles-col">{t('doubles')}</th>
              <th className="singles-col">{t('singles')}</th>
              <th className="total-col">{t('total')}</th>
            </tr>
          </thead>
          <tbody>
            {state.allPlayers.map(player => {
              const stats = playerStats.find(s => s.playerId === player.id);
              const pauses = stats?.pauseCount || 0;
              const doubles = stats?.doublesCount || 0;
              const singles = stats?.singlesCount || 0;
              const total = doubles + singles;
              
              return (
                <tr key={player.id} className="stats-row">
                  <td className="player-name-cell">{player.name}</td>
                  <td className="pauses-cell">{pauses}</td>
                  <td className="doubles-cell">
                    <span className={`stat-number ${doubles > 0 ? 'has-value' : ''}`}>
                      {doubles}
                    </span>
                  </td>
                  <td className="singles-cell">
                    <span className={`stat-number ${singles > 0 ? 'has-value' : ''}`}>
                      {singles}
                    </span>
                  </td>
                  <td className="total-cell">{total}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Partnership Matrix Component
interface PartnershipMatrixProps {
  controller: MatchGeneratorController;
}

const PartnershipMatrix: React.FC<PartnershipMatrixProps> = ({ controller }) => {
  const { t } = useTranslation();
  const [state, setState] = useState(controller.getState());

  useEffect(() => {
    const unsubscribe = controller.subscribe(setState);
    return unsubscribe;
  }, [controller]);

  const playerStats = controller.getFilteredPlayerStatistics();

  if (state.allPlayers.length < 2) {
    return (
      <div className="partnership-matrix">
        <div className="matrix-header">
          <FontAwesomeIcon icon={faUsers} />
          <span className="matrix-title">{t('partnershipMatrix')}</span>
        </div>
        <div className="empty-matrix">
          <p>{t('needAtLeast2Players')}</p>
        </div>
      </div>
    );
  }

  const getPartnershipCount = (player1Id: string, player2Id: string): number => {
    const player1Stats = playerStats.find(s => s.playerId === player1Id);
    return player1Stats?.partnerHistory.get(player2Id) || 0;
  };

  const getPartnershipColor = (count: number): string => {
    if (count === 0 || count === 1) return 'partnership-rare';     // Green
    if (count === 2) return 'partnership-moderate';                // Orange  
    return 'partnership-frequent';                                 // Red
  };

  const getPartnershipLabel = (count: number): string => {
    if (count === 0 || count === 1) return '0-1x';
    if (count === 2) return '2x';
    return '3x+';
  };

  return (
    <div className="partnership-matrix">
      <div className="matrix-header">
        <FontAwesomeIcon icon={faUsers} />
        <span className="matrix-title">{t('partnershipMatrix')}</span>
      </div>

      {/* Legend */}
      <div className="matrix-legend">
        <div className="legend-item">
          <div className="legend-color partnership-rare"></div>
          <span className="legend-text">0-1x {t('newRarePartnership')}</span>
        </div>
        <div className="legend-item">
          <div className="legend-color partnership-moderate"></div>
          <span className="legend-text">2x {t('moderatePartnership')}</span>
        </div>
        <div className="legend-item">
          <div className="legend-color partnership-frequent"></div>
          <span className="legend-text">3x+ {t('frequentPartnership')}</span>
        </div>
      </div>

      {/* Matrix Table */}
      <div className="matrix-table-container">
        <table className="partnership-table">
          <thead>
            <tr>
              <th className="empty-corner"></th>
              {state.allPlayers.map(player => (
                <th key={player.id} className="player-header">
                  {player.name.substring(0, 4)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {state.allPlayers.map((rowPlayer, rowIndex) => (
              <tr key={rowPlayer.id}>
                <td className="player-label">{rowPlayer.name.substring(0, 8)}</td>
                {state.allPlayers.map((colPlayer, colIndex) => {
                  if (rowIndex === colIndex) {
                    // Self cell - show dash
                    return (
                      <td key={colPlayer.id} className="partnership-cell self-cell">
                        -
                      </td>
                    );
                  } else {
                    const count = getPartnershipCount(rowPlayer.id, colPlayer.id);
                    return (
                      <td 
                        key={colPlayer.id} 
                        className={`partnership-cell ${getPartnershipColor(count)}`}
                        title={`${rowPlayer.name} & ${colPlayer.name}: ${count} times`}
                      >
                        {count}
                      </td>
                    );
                  }
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}; 